/*
 * Rotating trust badges (mobile only).
 *
 * On a phone a badge strip either wraps its labels (product page) or wraps onto
 * one row per badge (home page), costing real vertical space above the fold.
 * This shows one badge at a time, full width, and cycles through them.
 *
 * The item selector is configurable via data-item-selector, so the same element
 * drives both strips.
 *
 * Every badge stays in the DOM and in the accessibility tree — only opacity
 * changes — so screen readers still announce all three, and the layout is a
 * CSS grid stack sized by the tallest badge, so cycling causes no layout shift.
 */
if (!customElements.get('rotating-badges')) {
  customElements.define(
    'rotating-badges',
    class RotatingBadges extends HTMLElement {
      constructor() {
        super();
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onMediaChange = this.onMediaChange.bind(this);
        this.pause = this.pause.bind(this);
        this.resume = this.resume.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
      }

      connectedCallback() {
        // Two different badge strips use this: the product page's
        // icon-with-text block and the home page's trust-badges section.
        const itemSelector = this.dataset.itemSelector || '.icon-with-text__item';
        this.items = [...this.querySelectorAll(itemSelector)];
        this.index = 0;
        this.interval = parseInt(this.dataset.interval, 10) || 4000;

        if (this.items.length < 2) return;

        this.mediaQuery.addEventListener('change', this.onMediaChange);
        this.reducedMotion.addEventListener('change', this.onMediaChange);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        // Pausing on interaction is the accessible-carousel requirement for
        // content that animates on its own.
        this.addEventListener('pointerenter', this.pause);
        this.addEventListener('pointerleave', this.resume);
        this.addEventListener('pointerdown', this.pause);
        this.addEventListener('focusin', this.pause);
        this.addEventListener('focusout', this.resume);

        this.onMediaChange();
      }

      disconnectedCallback() {
        this.mediaQuery.removeEventListener('change', this.onMediaChange);
        this.reducedMotion.removeEventListener('change', this.onMediaChange);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stop();
      }

      get shouldRotate() {
        return this.mediaQuery.matches && !this.reducedMotion.matches;
      }

      onMediaChange() {
        if (this.shouldRotate) {
          this.classList.add('rotating-badges--active');
          this.show(this.index);
          this.start();
        } else {
          // Above the breakpoint, or when the visitor has asked for reduced
          // motion, fall back to the normal row with every badge visible.
          this.stop();
          this.classList.remove('rotating-badges--active');
          this.items.forEach((item) => item.classList.remove('is-active'));
        }
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.stop();
        } else {
          this.start();
        }
      }

      start() {
        this.stop();
        // Single place that decides whether the timer may run, so callers
        // (media change, resume, visibility change) cannot disagree.
        if (!this.shouldRotate || this.paused || document.hidden) return;

        this.timer = setInterval(() => {
          this.index = (this.index + 1) % this.items.length;
          this.show(this.index);
        }, this.interval);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }

      pause() {
        this.paused = true;
        this.stop();
      }

      resume() {
        this.paused = false;
        this.start();
      }

      show(index) {
        this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      }
    }
  );
}
